import { Component, OnInit, Input } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { AppState } from '../../angular-wallet-base/store/appState';
import { getKeychain } from '../../angular-wallet-base/store/wallet';
import { SetVirtualCardDataAction } from '../../angular-wallet-base/actions/virtualDebitCard.actions';
import { AGX_KEYRING_ID, AUTH_REASON } from '../../angular-wallet-base/constants';
import { getTokenInfo, satoshiToNum, isEnterKey } from '../../angular-wallet-base/utils';
import { PriceOracleService } from '../../angular-wallet-base/services/price-oracle.service';
import { VirtualDebitCardService } from '../../angular-wallet-base/services/virtual-debit-card.service';
import { take } from 'rxjs/operators';
import { SubscriptionListenerComponent } from '../../angular-wallet-base/SubscriptionListenerComponent';
import { getTokenKeyring } from '../../angular-wallet-base/store/wallet';
import { TransactionSenderService } from '../../angular-wallet-base/services/transaction-sender.service';

@Component({
  selector: 'vdc-fund-form',
  templateUrl: './vdc-fund-form.component.html',
  styleUrls: ['./vdc-fund-form.component.scss'],
})

export class VdcFundFormComponent extends SubscriptionListenerComponent implements OnInit {
  @Input() epin: number;
  public epinBalance;
  public convRate = 1;
  public amount: number = 0;  // USD price
  public inputAgx: number;   // AGX input
  public inputUsd = 0;
  public initFeeAgx = 0;
  public initFeeUsd = 0;
  public reloadFeeAgx = 0;
  public reloadFeeUsd = 0;
  public totalAgx = 0;
  public totalUsd = 0;
  public txAmount = 10;
  public buyBackPrice = 0;
  public baseCurrency = 'USD';
  public agxInfo;
  public assetBalance;        // AGX balance
  public address;
  public assetSymbol;
  public variableFeeRate;
  public operationType;
  public noSys = false;
  public cardLimit;
  public maxUsdCreateLimit = 750;
  public maxCardBalance = 2500;

  constructor(private modal: ModalController,
              protected store: Store<AppState>,
              private nav: NavController,
              private virtualDebitCardService: VirtualDebitCardService,
              private transactionSender: TransactionSenderService,
              private priceOracleService: PriceOracleService) {
                super()
    this.store.select(getKeychain).pipe(take(1)).toPromise().then(async keyring => {
      // Get AGX Info
      this.agxInfo = getTokenInfo(keyring, AGX_KEYRING_ID);
      this.assetBalance = satoshiToNum(this.agxInfo.balance);
      this.address = this.agxInfo.address;
      this.assetSymbol = this.agxInfo.symbol;
      this.buyBackPrice =  await this.priceOracleService.getBuyBackPrice(this.baseCurrency,this.assetSymbol.toLowerCase());
      const retailValueBn =  await this.priceOracleService.getRetailFiatValue(this.assetSymbol.toLowerCase());
      this.convRate = retailValueBn.toNumber();
      const feeObj = await this.virtualDebitCardService.getFees();
      this.operationType = this.epin === 0 ? 'create' : 'reload';

      if (this.operationType === 'create') {
        // Create card
        this.initFeeUsd = Number(feeObj.fixed.create);
        this.initFeeAgx = Number(this.usdToAgx(this.initFeeUsd));
        this.variableFeeRate = feeObj.variable.create;
      } else {
        // Reload card
        this.variableFeeRate = feeObj.variable.reload;
      }
    });
  }

  async ngOnInit() {
    if (this.epin && this.operationType === 'reload') {
      this.cardLimit = await this.virtualDebitCardService.checkCardLimits(this.epin);
      this.epinBalance = await this.virtualDebitCardService.checkCardBalance(this.epin);
    }

    this.subscriptions.add(this.store.select(getTokenKeyring, { keyringId: 'SYS' }).subscribe(async keyring => {
      const { balance } = keyring;
      const sjsKit = await this.transactionSender.getSignAndSendKit('SYS');
      const estimatedFee = Number(await sjsKit.sjs.utils.fetchEstimateFee(sjsKit.backendURL, 6));

      if (estimatedFee >= balance) {
        this.noSys = true;
        return;
      }
    }))
  }

  async onSubmit() {
    this.store.dispatch(new SetVirtualCardDataAction({
      amount: Number(this.totalAgx.toFixed(2)),
      address: this.address,
      operationType: this.operationType,
      epin: this.epin
    }));

    if (this.operationType === 'create') {
      await this.nav.navigateForward(['/auth', AUTH_REASON.VIRTUAL_DEBIT_CARD_CREATE]);
    } else {
      await this.nav.navigateForward(['/auth', AUTH_REASON.VIRTUAL_DEBIT_CARD_FUND]);
      this.closeModal();
    }
  }

  assetPriceChange(agx) {
    this.updateFee(agx);
    this.inputUsd = Number(this.agxToUsd(agx));
    this.totalAgx = this.inputAgx + this.initFeeAgx + this.reloadFeeAgx;
    this.totalUsd = Number(this.agxToUsd(this.totalAgx));
  }

  updateFee(inputAgx) {
    this.reloadFeeAgx = inputAgx * this.variableFeeRate;
    this.reloadFeeUsd = Number(this.agxToUsd(this.reloadFeeAgx));
  }

  agxToUsd(agx) {
    return (agx * this.buyBackPrice).toFixed(8);
  }

  usdToAgx(usd) {
    return (usd / this.buyBackPrice).toFixed(8);
  }

  async closeModal() {
    await this.modal.dismiss();
  }

  checkAmount(num: number) {
    return this.virtualDebitCardService.checkReloadAmount(num);
  }

  onKeyUp(ev) {
    if (isEnterKey(ev) && !this.isSubmitDisabled()) {
      this.onSubmit();
    }
  }

  isSubmitDisabled() {
    return (this.noSys || this.totalAgx <= 0 || !this.inputAgx || this.assetBalance < this.totalAgx || !this.checkAmount(this.totalUsd))
  }

  isSubmitDisabledByCardLimit() {
    return this.operationType === 'reload' && this.cardLimit ? this.inputUsd > this.cardLimit.dailyLimit : false;
  }

  isSubmitDisabledByCardCreationLimit() {
    return this.operationType === 'create' ? this.inputUsd > this.maxUsdCreateLimit : false;
  }

  isSubmitDisabledByReachingBalanceLimit() {
    return (this.inputUsd + this.epinBalance) > this.maxCardBalance;
  }
}
